import * as React from 'react';
import { Typography, Box, Container, Button, Avatar, Chip, Card, Grid, Divider } from '@mui/material';
import { connect } from "react-redux";
import { useParams } from 'react-router-dom';
import { centered_flex_box, main_button } from '../../app/components/Styles';
import { useEffect } from 'react';
import { getVendors, editVendors, getVendorDashboard } from '../../app/store/actions/imsAdminActions';
import { AccountCircle, Inventory, Receipt, Store, Email, LocalShipping, CheckCircle, Cancel, Business, AccountBalance, Notifications, Settings, Palette, TrendingUp, CheckCircle as CheckIcon, Cancel as CancelIcon, Inventory2 } from '@mui/icons-material';
import moment from 'moment';
import EditBrandModal from '../../app/components/EditBrandModal';

export const Brand = ({ getVendors, editVendors, getVendorDashboard, vendor, partner, vendorDashboard, isLoading }) => {

    const { brandId } = useParams()
    const [openEditModal, setOpenEditModal] = React.useState(false);

    useEffect(() => {
        getVendors({ vendorId: brandId })
        getVendorDashboard({ vendorId: brandId })
    }, [])

    const handleEdit = (settings) => {
        Promise.resolve(editVendors({
            vendorId: brandId,
            edits: settings
        })).then(() => {
            getVendors({ vendorId: brandId })
        })
        setOpenEditModal(false)
    }

    const InfoRow = ({ label, value, icon }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            {icon && <Box sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>{icon}</Box>}
            <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: 140, fontWeight: 500 }}>
                {label}:
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {value || 'N/A'}
            </Typography>
        </Box>
    );

    const StatusChip = ({ enabled, label }) => (
        <Chip
            icon={enabled ? <CheckCircle /> : <Cancel />}
            label={label}
            color={enabled ? 'success' : 'default'}
            size="small"
            sx={{ mr: 1, mb: 0.5 }}
        />
    );

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            {/* Header Section */}
            <Card sx={{ mb: 3, p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                        {vendor?.logo ? (
                            <Avatar
                                src={vendor.logo}
                                sx={{ width: 80, height: 80 }}
                                variant="rounded"
                            />
                        ) : (
                            <Avatar sx={{ width: 80, height: 80, bgcolor: 'primary.main' }} variant="rounded">
                                <Store />
                            </Avatar>
                        )}
                        <Box>
                            <Typography variant="h4" sx={{ color: "var(--secColor)", fontWeight: 600, mb: 0.5 }}>
                                {vendor?.brandName || vendor?.name || 'N/A'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Created: {vendor?.createdAt ? moment(vendor.createdAt).format('lll') : 'N/A'}
                            </Typography>
                        </Box>
                    </Box>
                    <Button
                        variant="contained"
                        onClick={() => setOpenEditModal(true)}
                        sx={{ ...main_button }}
                        startIcon={<Settings />}
                    >
                        Edit Brand Details
                    </Button>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="h6" sx={{ mb: 2, color: "var(--secColor)", display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AccountCircle /> Brand Information
                        </Typography>
                        <InfoRow label="Brand Name" value={vendor?.brandName} />
                        <InfoRow label="Name" value={vendor?.name} />
                        <InfoRow label="Email" value={vendor?.email} />
                        <InfoRow label="Directory" value={vendor?.directory} />
                        <InfoRow label="Shipping Service" value={vendor?.shippingService} />
                        {vendor?.monthOrders !== undefined && (
                            <InfoRow label="Orders This Month" value={vendor.monthOrders} />
                        )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Typography variant="h6" sx={{ mb: 2, color: "var(--secColor)", display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Palette /> Brand Colors
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Primary Color</Typography>
                                <Box
                                    sx={{
                                        width: 60,
                                        height: 40,
                                        bgcolor: vendor?.primaryColor || '#FFFFFF',
                                        border: '1px solid #ddd',
                                        borderRadius: 1,
                                        mt: 0.5
                                    }}
                                />
                                <Typography variant="caption">{vendor?.primaryColor || '#FFFFFF'}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Secondary Color</Typography>
                                <Box
                                    sx={{
                                        width: 60,
                                        height: 40,
                                        bgcolor: vendor?.secondaryColor || '#F7463C',
                                        border: '1px solid #ddd',
                                        borderRadius: 1,
                                        mt: 0.5
                                    }}
                                />
                                <Typography variant="caption">{vendor?.secondaryColor || '#F7463C'}</Typography>
                            </Box>
                        </Box>
                        <Typography variant="h6" sx={{ mb: 2, mt: 3, color: "var(--secColor)", display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Settings /> Fulfillment Settings
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                            These settings can be edited using the button above
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            <StatusChip enabled={vendor?.allowToOpenPackage} label="Allow Open Package" />
                            <StatusChip enabled={vendor?.fragileOrders} label="Fragile Orders" />
                            <StatusChip enabled={vendor?.receiveOrders} label="Receive Orders" />
                            <StatusChip enabled={vendor?.usesEcom} label="Uses EcomHQ" />
                        </Box>
                    </Grid>
                </Grid>
            </Card>

            {/* Vendor Dashboard Statistics */}
            {vendorDashboard && (
                <Card sx={{ mb: 3, p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 3, color: "var(--secColor)", display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrendingUp /> Vendor Statistics
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.light', bgcolor: 'rgba(25, 118, 210, 0.08)' }}>
                                <Inventory2 sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                                <Typography variant="h4" sx={{ fontWeight: 600, color: "var(--secColor)", mb: 0.5 }}>
                                    {vendorDashboard.ordersLeftToPack || 0}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Orders Left to Pack
                                </Typography>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: 'rgba(156, 39, 176, 0.08)' }}>
                                <LocalShipping sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
                                <Typography variant="h4" sx={{ fontWeight: 600, color: "var(--secColor)", mb: 0.5 }}>
                                    {vendorDashboard.ordersLeftToSend || 0}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Orders Left to Send
                                </Typography>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: 'rgba(46, 125, 50, 0.08)' }}>
                                <CheckIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                                <Typography variant="h4" sx={{ fontWeight: 600, color: "var(--secColor)", mb: 0.5 }}>
                                    {vendorDashboard.deliveredOrdersThisMonth || 0}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Delivered This Month
                                </Typography>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: 'rgba(211, 47, 47, 0.08)' }}>
                                <CancelIcon sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
                                <Typography variant="h4" sx={{ fontWeight: 600, color: "var(--secColor)", mb: 0.5 }}>
                                    {vendorDashboard.rtoOrdersThisMonth || 0}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    RTO Orders This Month
                                </Typography>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: 'rgba(255, 152, 0, 0.08)' }}>
                                <TrendingUp sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                                <Typography variant="h4" sx={{ fontWeight: 600, color: "var(--secColor)", mb: 0.5 }}>
                                    {vendorDashboard.totalOrdersThisMonth || 0}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Total Orders This Month
                                </Typography>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: 'rgba(46, 125, 50, 0.08)' }}>
                                <CheckCircle sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                                <Typography variant="h4" sx={{ fontWeight: 600, color: "var(--secColor)", mb: 0.5 }}>
                                    {vendorDashboard.successRate ? `${vendorDashboard.successRate.toFixed(1)}%` : '0%'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Success Rate
                                </Typography>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: 'rgba(25, 118, 210, 0.08)' }}>
                                <Inventory sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                                <Typography variant="h4" sx={{ fontWeight: 600, color: "var(--secColor)", mb: 0.5 }}>
                                    {vendorDashboard.piecesInStock || 0}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Pieces in Stock
                                </Typography>
                            </Card>
                        </Grid>
                    </Grid>
                </Card>
            )}

            {/* Partner & Shopify Information */}
            {partner && (
                <Card sx={{ mb: 3, p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: "var(--secColor)", display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Business /> Partner & Shopify Information
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <InfoRow label="Partner Name" value={partner?.name} />
                            <InfoRow label="Partner Email" value={partner?.email} />
                            <InfoRow label="Shopify Domain" value={partner?.shopifyDomain} />
                            <InfoRow label="Shopify Service ID" value={partner?.shopifyServiceId} />
                            <InfoRow label="Shopify Service Location" value={partner?.shopifyServiceLocation} />
                            <Box sx={{ mb: 1.5 }}>
                                <StatusChip enabled={partner?.sddEnabled} label="Same-Day Delivery (Blink)" />
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <InfoRow label="Default Warehouse" value={partner?.defaultWarehouse} />
                            <InfoRow label="Return Warehouse" value={partner?.returnWarehouse} />
                            <InfoRow label="Created At" value={partner?.createdAt ? moment(partner.createdAt).format('lll') : 'N/A'} />
                            <InfoRow label="Updated At" value={partner?.updatedAt ? moment(partner.updatedAt).format('lll') : 'N/A'} />
                        </Grid>
                    </Grid>
                </Card>
            )}

            {/* Bank Account Information */}
            {vendor?.bankAccount && (
                <Card sx={{ mb: 3, p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: "var(--secColor)", display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccountBalance /> Bank Account Information
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <InfoRow label="Account Name" value={vendor.bankAccount.name} />
                            <InfoRow label="Bank" value={vendor.bankAccount.bank} />
                            <InfoRow label="IBAN" value={vendor.bankAccount.iban} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <InfoRow label="SWIFT Code" value={vendor.bankAccount.swift} />
                        </Grid>
                    </Grid>
                </Card>
            )}

            {/* Backtrack Information */}
            <Card sx={{ mb: 3, p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, color: "var(--secColor)", display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Receipt /> Backtrack
                </Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <StatusChip enabled={vendor?.hasReturnSystem} label="Has Backtrack" />
                        {vendor?.hasReturnSystem && (
                            <>
                                <InfoRow
                                    label="Subscription Date"
                                    value={vendor?.returnSystemSubscriptionDate ? moment(vendor.returnSystemSubscriptionDate).format('lll') : 'N/A'}
                                />
                                <StatusChip enabled={vendor?.returnSystemAttachmentsRequired} label="Attachments Required" />
                            </>
                        )}
                    </Grid>
                </Grid>
            </Card>

            {/* Notification Settings */}
            <Card sx={{ mb: 3, p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, color: "var(--secColor)", display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Notifications /> Notification Settings
                </Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <StatusChip enabled={vendor?.sendNotificationsOnEmail} label="Send Email Notifications" />
                        <StatusChip enabled={vendor?.sendDailyDigestNotifications} label="Daily Digest" />
                        <StatusChip enabled={vendor?.sendTransactionNotifications} label="Transaction Notifications" />
                        <StatusChip enabled={vendor?.sendWeeklyDigestNotifications} label="Weekly Digest" />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        {vendor?.dailyDigestNotificationsTypes && vendor.dailyDigestNotificationsTypes.length > 0 && (
                            <Box>
                                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, fontWeight: 500 }}>
                                    Daily Digest Types:
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {vendor.dailyDigestNotificationsTypes.map((type) => (
                                        <Chip key={type} label={type} size="small" />
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </Grid>
                </Grid>
            </Card>

            <EditBrandModal
                open={openEditModal}
                handleClose={() => setOpenEditModal(false)}
                handleAccept={handleEdit}
                title="Edit Brand Details"
                brand={vendor}
            />
        </Container>
    );
}

const mapStateToProps = (state) => ({
    vendor: state?.records?.vendor,
    partner: state?.records?.partner,
    vendorDashboard: state?.records?.vendorDashboard,
    isLoading: state?.records?.isLoading || state?.wait?.isLoading
});

const mapDispatchToProps = { getVendors, editVendors, getVendorDashboard };

export default connect(mapStateToProps, mapDispatchToProps)(Brand);